import { useEffect, useLayoutEffect, useRef, useState } from 'react'

import {
  EMPTY_LIST,
  EMPTY_OBJECT,
  EMPTY_STRING,
} from '../../../constants/common'
import {
  CURRENCY_DEFAULT,
  CURRENCY_SYMBOL_MAP,
} from '../../../constants/currency'
import { InputSelect } from '../../Select'

import { CURRENCY_SELECT_DROPDOWN_FLOATING_CONFIG } from './CurrencySelect.constants'
import {
  getFixedFractionMoneyInputChange,
  getMoneyInputChange,
  rawToMoneyDisplay,
} from './CurrencySelect.utils'
import { CurrencySelectControlContent } from './CurrencySelectControlContent'

function CurrencySelect({
  controlProps = EMPTY_OBJECT,
  currencyOptions = EMPTY_LIST,
  disabled,
  dropdownFloatingConfig = CURRENCY_SELECT_DROPDOWN_FLOATING_CONFIG,
  minimumFractionDigits = 0,
  placeholder,
  qaName,
  value,
  onChange,
  ...restProps
}) {
  const amount = value?.amount ?? EMPTY_STRING
  const currency = value?.currency ?? CURRENCY_DEFAULT

  const inputRef = useRef(null)
  const lastEmittedRef = useRef(amount)
  const lastFractionDigitsRef = useRef(minimumFractionDigits)

  const [displayValue, setDisplayValue] = useState(() =>
    rawToMoneyDisplay(amount, minimumFractionDigits)
  )
  // a fresh object per keystroke guarantees the restore effect runs even when
  // the formatted value is unchanged and React re-asserts it, which would
  // otherwise drop the caret to the end
  const [caretRequest, setCaretRequest] = useState(null)

  useEffect(() => {
    if (
      amount !== lastEmittedRef.current ||
      minimumFractionDigits !== lastFractionDigitsRef.current
    ) {
      setDisplayValue(rawToMoneyDisplay(amount, minimumFractionDigits))
      lastEmittedRef.current = amount
      lastFractionDigitsRef.current = minimumFractionDigits
    }
  }, [amount, minimumFractionDigits])

  useLayoutEffect(() => {
    if (caretRequest && inputRef.current) {
      inputRef.current.setSelectionRange(caretRequest.caret, caretRequest.caret)
    }
  }, [caretRequest])

  const currencyOptionLabel = currencyOptions.find(
    option => option.value === currency
  )?.label

  const currencyValue = {
    label: CURRENCY_SYMBOL_MAP[currency] ?? currencyOptionLabel ?? currency,
    value: currency,
  }

  function handleChangeAmount({ target }) {
    const { caret, display, raw } =
      minimumFractionDigits > 0
        ? getFixedFractionMoneyInputChange({
            fractionDigits: minimumFractionDigits,
            previousValue: displayValue,
            selectionStart: target.selectionStart,
            value: target.value,
          })
        : getMoneyInputChange({
            selectionStart: target.selectionStart,
            value: target.value,
          })

    lastEmittedRef.current = raw

    setCaretRequest({ caret })
    setDisplayValue(display)
    onChange({ amount: raw, currency })
  }

  // on blur snap the typed value to the resting fixed-precision format
  // (`20` → `20,00`); free-format consumers keep the text as typed
  function handleBlur() {
    if (minimumFractionDigits > 0) {
      setDisplayValue(
        rawToMoneyDisplay(lastEmittedRef.current, minimumFractionDigits)
      )
    }
  }

  function handleClear() {
    setCaretRequest(null)
    lastEmittedRef.current = EMPTY_STRING

    setDisplayValue(EMPTY_STRING)
    onChange({ amount: EMPTY_STRING, currency })
  }

  function handleSelectItem(option) {
    onChange({ amount, currency: option?.value ?? currency })

    inputRef.current?.focus()
  }

  function handleClickContainer({ inputSelectRef }) {
    inputSelectRef.current?.focus()
  }

  function checkIsNeedToShowClearButton({ disabled }) {
    return !disabled && !!displayValue
  }

  return (
    <InputSelect
      {...restProps}
      controlProps={{
        ...controlProps,
        Content: controlProps.Content ?? CurrencySelectControlContent,
        borderless: controlProps.borderless ?? false,
        contentProps: {
          inputProps: { disabled, onBlur: handleBlur },
          inputValue: displayValue,
          placeholder,
          qaName,
          onChange: handleChangeAmount,
        },
        indicatorsProps: {
          ButtonArrow: null,
          checkIsNeedToShowClearButton,
        },
        inputRef,
        openDropdownOnClickContainer: false,
        onClickContainer: handleClickContainer,
      }}
      disabled={disabled}
      dropdownFloatingConfig={dropdownFloatingConfig}
      options={currencyOptions}
      value={currencyValue}
      onClear={handleClear}
      onSelectItem={handleSelectItem}
    />
  )
}

export default CurrencySelect
